import {
  BarChart3,
  DollarSign,
  MousePointerClick,
  TrendingUp,
  ArrowUp,
  ArrowDown,
  Clock,
  CalendarDays,
  CalendarCheck2,
  ChevronsUp
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { motion } from "framer-motion";
import NoticeBoard from "../Components/NoticeBoard";
import PrivacyPolicy from "../Components/PrivacyPolicy";
import SectionDivider from "../Components/Animations/SectionDivider";
import { useAuth } from "../Auth/AuthContext";
import WelcomeBanner from "../Components/Animations/WelcomeBanner";
import { useEffect, useState } from "react";
import DashboardApiService from "../Services/dashboardApiService";

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchDashboardMetrics() {
      try {
        
        if (isAdmin) {
          const data = await DashboardApiService.getMergedDashboard();
          setMetrics(data);
        } else {
        const data = await DashboardApiService.getMergedPublisherDashboard(user.publisherId);
          setMetrics(data);
        }

      } catch (err) {
        console.error("Error loading dashboard metrics", err);
      } finally {
        setLoading(false);
      }
    }

    fetchDashboardMetrics();
  }, []);

  const kpis = metrics ? [
    { name: "Revenue", value: `$${metrics.totalRevenue.toFixed(2)}`, icon: DollarSign, color: "text-green-500" },
    { name: "eCPM", value: `$${metrics.avgECPM.toFixed(2)}`, icon: TrendingUp, color: "text-blue-500" },
    { name: "Ad Impressions", value: metrics.totalImpressions.toLocaleString(), icon: BarChart3, color: "text-purple-500" },
    { name: "Ad Requests", value: metrics.totalAdRequests.toLocaleString(), icon: BarChart3, color: "text-indigo-500" },
    { name: "Fill Rate", value: `${metrics.avgFillRate.toFixed(2)}%`, icon: BarChart3, color: "text-orange-500" },
    { name: "Viewability (%)", value: `${metrics.avgViewability.toFixed(2)}%`, icon: TrendingUp, color: "text-teal-500" },
    { name: "Clicks", value: metrics.totalClicks.toLocaleString(), icon: MousePointerClick, color: "text-yellow-500" },
    { name: "CTR (%)", value: `${metrics.avgCTR.toFixed(2)}%`, icon: TrendingUp, color: "text-pink-500" },
  ] : [];

  const iconMap = {
    Clock,
    CalendarDays,
    CalendarCheck2,
    ChevronsUp,
  };

  return (
    <div className="space-y-8">
      {user && <WelcomeBanner name={user.email.split("@")[0]} />}

      {/* Revenue Period Cards */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4"
      >
        {metrics?.revenuePeriods?.map((item, index) => {
          const IconComponent = iconMap[item.icon] || Clock;
          return (
            <motion.div
              key={item.title}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              whileHover={{ scale: 1.02 }}
              className="relative"
            >
              <div className="absolute inset-0 bg-white/50 backdrop-blur-sm rounded-xl shadow-sm border border-white/20" />
              <div className="relative p-4 h-full rounded-xl">
                <div className="flex flex-col h-full">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${item.iconColor} mb-3`}>
                    <IconComponent className="w-4 h-4" />
                  </div>
                  <h3 className="text-xs font-medium text-gray-500 mb-1">{item.title}</h3>
                  <p className="text-xl font-bold text-gray-800 mb-2">${item.amount.toFixed(2)}</p>
                  <div className="mt-auto pt-2 border-t border-gray-100/50">
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-medium ${item.trendColor} flex items-center`}>
                        {item.isPositive ? (
                          <ArrowUp className="w-3 h-3 mr-1" />
                        ) : (
                          <ArrowDown className="w-3 h-3 mr-1" />
                        )}
                        {item.change}
                      </span>
                      <span className="text-xs text-gray-400">{item.period}</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </motion.div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {kpis.map((item, index) => (
          <motion.div
            key={item.name}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            whileHover={{ scale: 1.02 }}
            className="relative"
          >
            <div className="absolute inset-0 bg-white/50 backdrop-blur-sm rounded-xl shadow-sm border border-white/20" />
            <div className="relative p-4 rounded-xl">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs font-medium text-gray-500">{item.name}</p>
                  <h2 className="mt-1 text-xl font-bold text-gray-800">{item.value}</h2>
                </div>
                <div className={`p-2 rounded-lg ${item.color.replace("text", "bg") + "100"} ${item.color}`}>
                  <item.icon className="w-4 h-4" />
                </div>
              </div>
              <div className="mt-3">
                <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${item.color.replace("text", "bg")} rounded-full`}
                    style={{ width: `${Math.random() * 100}%` }}
                  />
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <SectionDivider label="Revenue Analytics" />

      {/* Revenue Chart */}
      <div className="relative bg-white/50 backdrop-blur-sm p-4 rounded-xl border border-white/20 shadow-sm overflow-hidden">
        <div className="relative">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
            <div>
              <h3 className="text-lg font-bold text-gray-800">Revenue Trend</h3>
              <p className="text-xs text-gray-500">Daily revenue performance</p>
            </div>
            <div className="flex space-x-1">
              <button className="px-2 py-1 text-xs bg-indigo-500 text-white rounded-lg border border-indigo-500 hover:bg-indigo-600 transition-colors">
                7D
              </button>
              {/* Future buttons (30D, YTD) */}
            </div>
          </div>
          <div className="h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={metrics?.revenueTrend || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="revenue" stroke="#4f46e5" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <NoticeBoard />
        <PrivacyPolicy />
      </div>
    </div>
  );
}
